/**
 * This component is used to render the tabular view of the bifrost element.
 * Which includes tab labels, setting button, and content.
 * @name BifrostTab
 * @param {Object} props
 * @param {Array} props.data - The data array contains the all information for the bifrost element.
 * @param {Object} props.children - The children object contains the all children components.
 */

import React, { useCallback, useEffect, useState } from "react"
import { Dropdown, DropdownMenu, Icon, Label, Menu, MenuItem, MenuMenu, Segment, SegmentGroup } from "semantic-ui-react"
import { ReactComponent as OptionsIcon } from "../../../Icons/options.svg"
import UpgradePackageModal from "../../../Pages/Sidebar/SidebarNavPanel/SidebarNavLink/UpgradePackageModal/UpgradePackageModal"
import BifrostButton from "../../BifrostElementComponent/BifrostButton/BifrostButton"
import Loader from "../../BifrostPageComponent/Loader/Loader"

const BifrostTab = ({ detailData, settings, style, setActiveTab }) => {
	const [openUpgradePackageModal, setOpenUpgradePackageModal] = useState(false)
	const [feature, setFeature] = useState(null)
	const [activeItem, setActiveItem] = useState("")

	/**
	 * When the user click the tab, it will check the feature of the tab.
	 * if the feature is null or true, then set the tab as active. if the item has the fetchData function, then call the fetchData function once.
	 * if the feature is false, then set the feature name for the upgrade package modal and open the upgrade package modal.
	 * @param {object} item the render tab object.
	 */
	const handleItemClick = (item) => {
		if (item.feature === null || item.feature === true) {
			setActiveItem(item.tabName)
			item.fetchData && item.fetchData()
		} else {
			setFeature(item.featureName)
			setOpenUpgradePackageModal(true)
		}
	}

	/**
	 * This function is use for check if the item has render condition and the render condition is ture,
	 * then do not set the active item. Which means the tab will not be showed up and can not be active.
	 */
	const handlerItemRenderCondition = useCallback(
		(data) => {
			for (const detail of data) {
				if (detail.renderCondition) {
					continue
				} else {
					setActiveItem(detail.tabName)
					break
				}
			}
		},
		[detailData]
	)

	/**
	 * When the component will mount, it will check the render condition of the detail data.
	 * If the render condition is true, then the tab will not be showed up.
	 * And we only need check once. So we use the empty array as the second parameter.
	 */
	useEffect(() => {
		handlerItemRenderCondition(detailData)
	}, [])

	useEffect(() => {
		setActiveTab && setActiveTab(setActiveItem)
	}, [setActiveTab])

	return (
		<>
			<div style={{ backgroundColor: "white" }}>
				<UpgradePackageModal openModal={openUpgradePackageModal} setOpenModal={setOpenUpgradePackageModal} feature={feature} />
				<Menu tabular={style.tabular} style={style.tabStyle}>
					{detailData.map(
						(item) =>
							!item.renderCondition && (
								<Menu.Item key={item.tabName} name={item.tabName} disabled={item.disabled} active={activeItem === item.tabName} onClick={() => handleItemClick(item)}>
									{item.feature !== null && (item.feature ? item.featureIcon ? <Icon name={item.featureIcon} /> : <Icon name="star outline" /> : <Icon color="grey" name="lock" />)}
									{item.icon && item.icon}
									{item.tabName}
									{item.isBetaFeature && <Label>Beta</Label>}
								</Menu.Item>
							)
					)}
					{settings && (
						<MenuMenu position="right">
							{settings.refreshs.length > 0 &&
								settings.refreshs.map(
									(item) =>
										activeItem === item.tab && (
											<MenuItem key={item.tab} className={"device-detail-options"} style={{ padding: "0px" }}>
												<BifrostButton
													onClick={() => {
														item.refresh()
													}}
													size="large"
													style={{ backgroundColor: "transparent", margin: "1px" }}
												>
													<Icon name="refresh" color="black" style={{ margin: "0px", opacity: "1" }} />
												</BifrostButton>
											</MenuItem>
										)
								)}
							<MenuItem className={"device-detail-options"} style={{ padding: "0px" }}>
								<Dropdown style={{ padding: "0px 8px" }} icon={<OptionsIcon style={{ height: "32px", width: "32px" }} />}>
									<DropdownMenu>{settings.options.map((setting) => setting)}</DropdownMenu>
								</Dropdown>
							</MenuItem>
						</MenuMenu>
					)}
				</Menu>
				{detailData.map(
					(item) =>
						activeItem === item.tabName && (
							<div key={item.tabName} style={style.contentStyle}>
								{item.isLoading ? (
									<SegmentGroup horizontal style={{ marginTop: "0px" }}>
										<Segment style={{ width: "50%" }}>
											<Loader backgroundColor="white" inverted height="1px" minHeight="200px" />
										</Segment>
									</SegmentGroup>
								) : (
									<>{item.tabContent}</>
								)}
							</div>
						)
				)}
			</div>
		</>
	)
}

export default BifrostTab
